/* eslint-disable @atlassian/relay/must-use-inline */
import { useCallback, useMemo } from 'react';
import { commitLocalUpdate, graphql, useClientQuery, useRelayEnvironment } from 'react-relay';
import type { useCurrentTab815BadPatternQuery } from './__generated__/useCurrentTab815BadPatternQuery.graphql';
import type { useCurrentTab452BadPatternQuery } from './__generated__/useCurrentTab452BadPatternQuery.graphql';
import type { useCurrentTabQuery } from './__generated__/useCurrentTabQuery.graphql';
import { useSidebarForm } from './FormProvider.tsx';
import { CURRENT_SIDEBAR_ID } from './sidebarIds.tsx';
import { useFieldNode } from './useFieldNode.tsx';

/**
 * Gets currentTab
 * @param queryRef
 * @returns
 */
export const useCurrentTab = () => {
	const clientData = useClientQuery<useCurrentTabQuery>(
		graphql`
			query useCurrentTabQuery {
				jira @required(action: THROW) {
					centralizedFieldsAdminPage {
						currentTab
					}
				}
			}
		`,
		{},
	);

	const centralizedFieldsAdminPage = clientData.jira.centralizedFieldsAdminPage;

	if (!centralizedFieldsAdminPage) {
		throw new Error('CentralizedFieldsAdminPage is not available');
	}

	return centralizedFieldsAdminPage.currentTab;
};

export const useCurrentSidebarId = () => {
	const environment = useRelayEnvironment();
	const clientData = useClientQuery<useCurrentTab815BadPatternQuery>(
		graphql`
			query useCurrentTab815BadPatternQuery {
				jira {
					centralizedFieldsAdminPage {
						currentSidebar {
							currentSidebarId
						}
					}
				}
			}
		`,
		{},
	);

	const setCurrentSidebarId = useCallback(
		(sidebarId: string | undefined) => {
			commitLocalUpdate(environment, (store) => {
				store.get(CURRENT_SIDEBAR_ID)?.setValue(sidebarId, 'currentSidebarId');
			});
		},
		[environment],
	);

	return useMemo(() => {
		return {
			currentSidebarId:
				clientData.jira?.centralizedFieldsAdminPage?.currentSidebar?.currentSidebarId ?? undefined,
			setCurrentSidebarId,
		};
	}, [
		clientData.jira?.centralizedFieldsAdminPage?.currentSidebar?.currentSidebarId,
		setCurrentSidebarId,
	]);
};

export const useCurrentSidebarFieldNodeId = () => {
	const environment = useRelayEnvironment();
	const clientData = useClientQuery<useCurrentTab452BadPatternQuery>(
		graphql`
			query useCurrentTab452BadPatternQuery {
				jira {
					centralizedFieldsAdminPage {
						currentSidebar {
							currentFieldNodeId
						}
					}
				}
			}
		`,
		{},
	);

	const setCurrentSidebarFieldNodeId = useCallback(
		(sidebarId?: string) => {
			commitLocalUpdate(environment, (store) => {
				store.get(CURRENT_SIDEBAR_ID)?.setValue(sidebarId, 'currentFieldNodeId');
			});
		},
		[environment],
	);

	return useMemo(() => {
		return {
			currentSidebarFieldNodeId:
				clientData.jira?.centralizedFieldsAdminPage?.currentSidebar?.currentFieldNodeId ??
				undefined,
			setCurrentSidebarFieldNodeId,
		};
	}, [
		clientData.jira?.centralizedFieldsAdminPage?.currentSidebar?.currentFieldNodeId,
		setCurrentSidebarFieldNodeId,
	]);
};

/**
 * Returns if the current sidebar is dirty
 * @returns boolean
 */
export const useIsCurrentSidebarDirty = () => {
	const { currentSidebarId } = useCurrentSidebarId();
	const { sidebarState } = useSidebarForm(currentSidebarId);
	return sidebarState?.dirty ?? false;
};

/**
 * Returns the current sidebar field node
 * @returns JiraIssueFieldConfig
 */

type UseCurrentSidebarFieldNodeProps = {
	fetchPolicy?: 'store-only' | 'network-only' | 'store-or-network';
};

export const useCurrentSidebarFieldNode = (options?: UseCurrentSidebarFieldNodeProps) => {
	const { currentSidebarFieldNodeId } = useCurrentSidebarFieldNodeId();
	const fieldNode = useFieldNode(currentSidebarFieldNodeId, options);
	return fieldNode ?? undefined;
};
